import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button, { ButtonProps } from 'ext-ui/components/Button/Button'
import React from 'react'
import { useDispatch } from 'react-redux'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { closeModal } from 'src/navigation/Lnk'
import { getReactNodeSourceString } from 'src/utils/reactNode.utils'
import { css } from 'styled-components'

type ConfirmationButtonVariant = ButtonProps['variant']

interface ConfirmationPopupContentProps {
  className?: string
  title: React.ReactNode
  subtitle: React.ReactNode
  actionButtonLabel: React.ReactNode
  actionButtonOnClick: () => void
  actionButtonLoading: boolean | undefined
  actionButtonVariant?: ConfirmationButtonVariant
  cancelButtonLabel?: React.ReactNode
  cancelButtonVariant?: ConfirmationButtonVariant
  cancelButtonOnClick?: () => void
  topLevelError?: string
  isCancelButtonLink?: boolean
  actionButtonDisabled?: boolean
  underline?: boolean
}

const ConfirmationPopupContent: React.FC<ConfirmationPopupContentProps> = (
  props,
): React.ReactNode => {
  const dispatch = useDispatch()

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 40px;
        `}
      >
        {props.title}
      </span>
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          margin-bottom: 40px;
        `}
      >
        {props.subtitle}
      </span>
      {props.topLevelError !== undefined && (
        <span
          css={css`
            margin-bottom: 40px;
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {props.topLevelError}
        </span>
      )}
      <div
        css={css`
          display: flex;
        `}
      >
        <Button
          variant={props.actionButtonVariant || 'severe'}
          type="button"
          onClick={props.actionButtonOnClick}
          loading={props.actionButtonLoading}
          disabled={props.actionButtonLoading || props.actionButtonDisabled}
          testId={'confirmBtn'}
        >
          {props.actionButtonLabel}
        </Button>
        {props.isCancelButtonLink ? (
          <div
            css={css`
              margin-top: 15px;
            `}
          >
            <HoverableLink
              css={css`
                margin-left: 20px;
              `}
              onClick={
                props.cancelButtonOnClick ||
                ((): void => {
                  dispatch(closeModal())
                })
              }
              underline={props.underline !== undefined ? props.underline : true}
              regular
              ariaLabel={
                getReactNodeSourceString(props.cancelButtonLabel) ?? 'Cancel'
              }
              size={HoverableLinkSizes.Large}
              testId={'cancelBtn'}
            >
              {props.cancelButtonLabel}
            </HoverableLink>
          </div>
        ) : (
          <Button
            css={css`
              margin-left: 20px;
            `}
            variant={props.cancelButtonVariant || 'secondary'}
            type="button"
            testId={'cancelBtn'}
            onClick={
              props.cancelButtonOnClick ||
              ((): void => {
                dispatch(closeModal())
              })
            }
          >
            {props.cancelButtonLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

export default ConfirmationPopupContent
